import * as React from 'react'
import PhoneNumberInput, {
  getCountryCallingCode,
  type Country,
} from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'
import {
  ComboBox as AriaComboBox,
  Input as AriaInput,
  Button as AriaButton,
  Group,
  Popover,
  ListBox,
  ListBoxItem,
} from 'react-aria-components'
import { CaretDown, Check } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import {
  fieldGroupStatus,
  useFieldDisabled,
  useFieldStatus,
  type FieldStatus,
} from './field'

/**
 * PhoneInput — an international phone control in the standard "flag + dial code"
 * layout (intl-tel-input / Stripe / Twilio): a compact country trigger showing
 * the flag and calling code sits attached to the left of the number field, in
 * one bordered shell. The number is entered in national format (the calling
 * code lives in the trigger, not duplicated in the field) and stored as E.164
 * (e.g. `+12025550123`).
 *
 *   <PhoneInput value={phone} onChange={setPhone} />
 *
 * Built on `react-phone-number-input` (formatting/parsing, per-country
 * max-length limiting, and bundled flag SVGs) with our own react-aria searchable
 * country dropdown swapped in as its `countrySelectComponent`, so it nests
 * correctly inside a react-aria `Dialog`. Presentational + controlled: number is
 * `value`/`onChange`; country is uncontrolled from `defaultCountry` unless you
 * pass `country`/`onCountryChange`. Status + disabled are inherited from a
 * wrapping `<Field>` (or set here). The number is length-capped per country
 * (`limitMaxLength`), so it always stays in the tidy national format and can't
 * run past a valid length; changing the country re-formats it rather than
 * wiping it. Set `withExtension` (or pass `onExtensionChange`) for a trailing
 * extension box.
 */

type PhoneSize = 'sm' | 'md' | 'lg'

interface CountryOption {
  value?: Country
  label: string
}

const shellHeight: Record<PhoneSize, string> = {
  sm: 'h-8 text-xs',
  md: 'h-9 text-sm',
  lg: 'h-10 text-sm',
}

/** Flag SVG for a country, sized to a small rounded rectangle. */
function CountryFlag({ country, title }: { country: Country; title: string }) {
  const Flag = flags[country]
  if (!Flag) return null
  return (
    <span className="h-3.5 w-5 shrink-0 overflow-hidden rounded-[2px] [&>svg]:h-full [&>svg]:w-full [&>svg]:object-cover">
      <Flag title={title} />
    </span>
  )
}

/**
 * Our country trigger + searchable dropdown, shaped to react-phone-number-input's
 * `countrySelectComponent` contract ({ value, onChange, options, disabled }). A
 * react-aria ComboBox whose input doubles as the search field: collapsed it
 * shows the dial code (flag as a leading adornment); focused, typing filters by
 * country name or code.
 */
function CountrySelect({
  value,
  onChange,
  options,
  disabled = false,
}: {
  value?: Country
  onChange: (country?: Country) => void
  options: CountryOption[]
  disabled?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')

  const labelFor = React.useMemo(() => {
    const map = new Map<Country, string>()
    for (const o of options) if (o.value) map.set(o.value, o.label)
    return map
  }, [options])

  const countries = React.useMemo(
    () =>
      options
        .filter((o): o is { value: Country; label: string } => Boolean(o.value))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [options]
  )

  const dialDisplay = value ? `+${getCountryCallingCode(value)}` : ''
  const trimmed = query.trim().toLowerCase().replace(/^\+/, '')
  const items = trimmed
    ? countries.filter(
        (c) =>
          c.label.toLowerCase().includes(trimmed) ||
          getCountryCallingCode(c.value).includes(trimmed)
      )
    : countries

  return (
    <AriaComboBox
      aria-label="Country calling code"
      isDisabled={disabled}
      selectedKey={value ?? null}
      // We filter `items` ourselves, so don't filter again on the client.
      defaultFilter={() => true}
      allowsEmptyCollection
      menuTrigger="focus"
      // Collapsed → dial code; open → the live search query.
      inputValue={open ? query : dialDisplay}
      onInputChange={(v) => {
        setQuery(v)
        if (!open) setOpen(true)
      }}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen) setQuery('')
      }}
      onSelectionChange={(key) => {
        if (key != null) onChange(String(key) as Country)
        setOpen(false)
        setQuery('')
      }}
      className="shrink-0"
    >
      <Group className="flex h-full items-center gap-1 border-r border-fbui-gray-200 pl-2.5 pr-0.5 outline-none data-[disabled]:cursor-not-allowed">
        {value && <CountryFlag country={value} title={labelFor.get(value) ?? value} />}
        <AriaInput
          // Select all on focus so typing replaces the dial code (search fresh)
          // instead of editing it mid-string.
          onFocus={(e) => e.currentTarget.select()}
          aria-label="Search country"
          // Dial codes are at most 3 digits ("+993"), so the input only needs to
          // fit "+XXX"; a longer search query just scrolls within it.
          className="w-9 min-w-0 bg-transparent text-fbui-gray-800 outline-none placeholder:text-fbui-gray-400 disabled:cursor-not-allowed tabular-nums"
        />
        <AriaButton className="inline-flex size-5 shrink-0 items-center justify-center rounded text-fbui-gray-500 outline-none transition-colors data-[hovered]:text-fbui-gray-600 data-[focus-visible]:ring-2 data-[focus-visible]:ring-fbui-ring-brand">
          <CaretDown weight="bold" className="size-3" aria-hidden="true" />
        </AriaButton>
      </Group>
      <Popover
        offset={6}
        className="z-50 w-72 overflow-hidden rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-1.5 shadow-fbui-lg outline-none"
      >
        <ListBox
          renderEmptyState={() => (
            <div className="px-[0.75rem] py-6 text-center text-sm text-fbui-gray-500">
              No country found
            </div>
          )}
          className="fbui-scroll max-h-72 w-full overflow-y-auto outline-none"
          items={items}
        >
          {(item) => (
            <ListBoxItem
              id={item.value}
              textValue={item.label}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 rounded-lg px-[0.75rem] py-2 text-sm text-fbui-gray-700 outline-none select-none',
                'data-[focused]:bg-fbui-gray-100 data-[focused]:text-fbui-gray-900',
                'data-[selected]:bg-fbui-basil-50 data-[selected]:font-medium data-[selected]:text-fbui-basil-700'
              )}
            >
              {(renderProps) => (
                <>
                  <CountryFlag country={item.value} title={item.label} />
                  <span className="flex-1 truncate">{item.label}</span>
                  <span className="shrink-0 text-fbui-gray-500 tabular-nums">
                    +{getCountryCallingCode(item.value)}
                  </span>
                  {renderProps.isSelected && (
                    <Check
                      weight="bold"
                      aria-hidden="true"
                      className="size-4 shrink-0 text-fbui-basil-700"
                    />
                  )}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaComboBox>
  )
}

export interface PhoneInputProps {
  /** The phone number in E.164 form (e.g. `+12025550123`). */
  value?: string
  onChange?: (value?: string) => void
  /** Controlled selected country (ISO code). Pair with `onCountryChange`. */
  country?: Country
  /** Initial country when uncontrolled. Defaults to `US`. */
  defaultCountry?: Country
  onCountryChange?: (country?: Country) => void
  size?: PhoneSize
  /** Border + ring color. Inherited from a parent `<Field>` when omitted. */
  status?: FieldStatus
  disabled?: boolean
  placeholder?: string
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  /** Show the trailing extension box. Implied when `onExtensionChange` is set. */
  withExtension?: boolean
  extension?: string
  onExtensionChange?: (extension: string) => void
  extensionPlaceholder?: string
  'aria-label'?: string
  className?: string
}

export const PhoneInput = React.forwardRef<
  // react-phone-number-input is a class component — its ref is the instance
  // (which exposes `.focus()`), not the raw input element.
  React.ElementRef<typeof PhoneNumberInput>,
  PhoneInputProps
>(
  (
    {
      value,
      onChange,
      country,
      defaultCountry = 'US',
      onCountryChange,
      size = 'md',
      status,
      disabled,
      placeholder = 'Phone number',
      onBlur,
      withExtension,
      extension,
      onExtensionChange,
      extensionPlaceholder = 'Ext.',
      'aria-label': ariaLabel = 'Phone number',
      className,
    },
    ref
  ) => {
    const resolvedStatus = useFieldStatus(status)
    const isDisabled = useFieldDisabled(disabled)

    const showExtension = withExtension ?? onExtensionChange !== undefined

    return (
      <div className={cn('flex w-full items-center gap-2', className)}>
        <PhoneNumberInput
          ref={ref}
          value={value}
          onChange={(next?: string) => onChange?.(next)}
          {...(country ? { country } : { defaultCountry })}
          onCountryChange={onCountryChange}
          onBlur={onBlur}
          disabled={isDisabled}
          // Cap the number at each country's max length: keeps it in the tidy
          // national format and stops it running past a valid length.
          limitMaxLength
          // Country-only list (no "International" pseudo-option).
          addInternationalOption={false}
          countrySelectComponent={CountrySelect}
          numberInputProps={{
            'aria-label': ariaLabel,
            className:
              'h-full min-w-0 flex-1 bg-transparent pl-2.5 pr-3 text-fbui-gray-800 outline-none placeholder:text-fbui-gray-400 disabled:cursor-not-allowed disabled:text-fbui-text-disabled tabular-nums',
          }}
          placeholder={placeholder}
          className={cn(
            'flex min-w-0 flex-1 items-center rounded-lg border bg-fbui-base-white transition focus-within:ring-2',
            shellHeight[size],
            fieldGroupStatus[resolvedStatus],
            isDisabled &&
              'cursor-not-allowed bg-fbui-gray-50 !border-fbui-gray-200'
          )}
        />

        {showExtension && (
          <input
            type="text"
            inputMode="numeric"
            aria-label="Extension"
            disabled={isDisabled}
            value={extension ?? ''}
            onChange={(e) => onExtensionChange?.(e.target.value)}
            onBlur={onBlur}
            placeholder={extensionPlaceholder}
            className={cn(
              'w-24 shrink-0 rounded-lg border bg-fbui-base-white px-3 text-fbui-gray-800 outline-none transition focus:ring-2 placeholder:text-fbui-gray-400 disabled:cursor-not-allowed disabled:bg-fbui-gray-50 disabled:text-fbui-text-disabled tabular-nums',
              shellHeight[size],
              fieldGroupStatus[resolvedStatus]
            )}
          />
        )}
      </div>
    )
  }
)
PhoneInput.displayName = 'PhoneInput'
